  /**
   * Fall Protection Plan Component
   * ===============================
   * 
   * Individual form component for fall protection plan submissions.
   * 
   * Base Components:
   * - EXTENDS BaseFormComponent<FallProtectionPlan> for form logic inheritance
   * - USES FormLayoutComponent for step navigation UI (in template)
   * 
   * Form Configuration:
   * - Form structure, steps, sections, and field organization are defined in the form model
   * - Signature and upload requirements are configured via form model metadata
   * - Component implements abstract methods: initializeForm() and getFormData()
   * - Component overrides setupDefaultStepValidation() for custom step validation rules
   * 
   * Key Functionality:
   * - Creates nested form groups for logical organization (basic, fallHazards, fallProtections, overheadProtection, emergencyProcedures)
   * - Implements multiple checkbox groups with validation requirements for safety compliance
   * - Provides conditional step rendering based on employee training status
   * - Implements dynamic form validation with conditional field requirements
   * - Flattens nested form structure for backend submission with conditional data inclusion
   * 
   * Unique Complex Logic:
   * - Conditional step management based on employee training status (employeesTrained field)
   * - Dynamic validation setup for "other" specification fields when corresponding checkboxes are selected
   * - Conditional form group inclusion in final submission based on training requirements
   * - Multiple checkbox group validators with minimum selection requirements
   * - Real-time form structure modification based on user input
   * 
   * Form Model vs Component Responsibilities:
   * - FORM MODEL: Defines step/section structure, field mappings, attachment requirements, and form metadata
   * - COMPONENT: Handles Angular form validation, form group creation, conditional field logic, and overrides base form methods
   * 
   * @author MacSafety Development Team
   * @version 1.0
   */


import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseFormComponent } from '../../base-form.component';
import { FormStep } from '../../form-layout.component';
import { FallProtectionPlan } from '../../../../../models/forms/fall-protection-plan/fall-protection-plan.model';
import { FormService } from '../../../../../services/form.service';
import { EntraService } from '../../../../../services/entra.service';
import { AttachmentUploadService } from '../../../../../services/attachment-upload.service';
import { SignatureService } from '../../../../../services/signature.service';
import { ToastService } from '../../../../../services/toast.service';
import { ScreenSizeService } from '../../../../../services/screen-size.service';
import { FormModelService } from '../../../../../services/form-model.service';
import { simpleCheckboxValidator } from '../../../../../utils/form-validators';

@Component({
  selector: 'app-fall-protection-plan',
  templateUrl: './fall-protection-plan.component.html',
  styleUrls: ['./fall-protection-plan.component.css'],
})
export class FallProtectionPlanComponent
  extends BaseFormComponent<FallProtectionPlan>
  implements OnInit
{
  override formType = FallProtectionPlan.getFormInfo().formType;
  override model = new FallProtectionPlan();
  override steps: FormStep[] = FallProtectionPlan.getFormSteps();

  // Additional steps that can be conditionally added
  additionalSteps: FormStep[] = [
    {
      id: 'overhead-protection',
      shortTitle: 'Overhead Protection',
      title: 'Overhead Protection',
      icon: 'shield-checkmark-outline',
      description: 'Document overhead protection measures',
    },
    {
      id: 'emergency-procedures',
      shortTitle: 'Emergency Procedures',
      title: 'Emergency Procedures',
      icon: 'medical-outline',
      description: 'Document emergency procedures',
    },
  ];

  fallHazardsCheckboxes: { name: string; label: string }[] = [];
  articulatedLiftsCheckboxes: { name: string; label: string }[] = [];
  fallRestraintCheckboxes: { name: string; label: string }[] = [];
  materialHandlingCheckboxes: { name: string; label: string }[] = [];
  overheadProtectionCheckboxes: { name: string; label: string }[] = [];
  emergencyProceduresCheckboxes: { name: string; label: string }[] = [];

  constructor(
    protected override fb: FormBuilder,
    protected override formService: FormService,
    protected override router: Router,
    protected override entraService: EntraService,
    protected override attachmentService: AttachmentUploadService,
    protected override toastService: ToastService,
    protected override signatureService: SignatureService,
    protected override screenSizeService: ScreenSizeService,
    protected formModelService: FormModelService
  ) {
    super(
      fb,
      formService,
      router,
      entraService,
      attachmentService,
      toastService,
      signatureService,
      screenSizeService
    );

    this.formMetadata = this.formModelService.getFormMetadata(this.formType)!;

  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  protected override initializeForm(): void {
    this.fallHazardsCheckboxes = this.getFallHazardsCheckboxes();
    this.articulatedLiftsCheckboxes = this.getArticulatedLiftsCheckboxes();
    this.fallRestraintCheckboxes = this.getFallRestraintCheckboxes();
    this.materialHandlingCheckboxes = this.getMaterialHandlingCheckboxes();
    this.overheadProtectionCheckboxes = this.getOverheadProtectionCheckboxes();
    this.emergencyProceduresCheckboxes =
      this.getEmergencyProceduresCheckboxes();

    // Create form groups for checkboxes with validators
    const fallHazardsGroup = this.fb.group(
      {},
      {
        validators: [simpleCheckboxValidator(1)], // Require at least 1 fall hazard
      }
    );
    this.fallHazardsCheckboxes.forEach((checkbox) => {
      fallHazardsGroup.addControl(checkbox.name, this.fb.control(false));
    });

    const fallRestraintGroup = this.fb.group(
      {},
      {
        validators: [simpleCheckboxValidator(1)], // Require at least 1 fall restraint method
      }
    );
    this.fallRestraintCheckboxes.forEach((checkbox) => {
      fallRestraintGroup.addControl(checkbox.name, this.fb.control(false));
    });

    const materialHandlingGroup = this.fb.group(
      {},
      {
        validators: [simpleCheckboxValidator(1)], // Require at least 1 material handling method
      }
    );
    this.materialHandlingCheckboxes.forEach((checkbox) => {
      materialHandlingGroup.addControl(checkbox.name, this.fb.control(false));
    });

    const overheadProtectionGroup = this.fb.group(
      {},
      {
        validators: [simpleCheckboxValidator(1)], // Require at least 1 overhead protection measure
      }
    );
    this.overheadProtectionCheckboxes.forEach((checkbox) => {
      overheadProtectionGroup.addControl(checkbox.name, this.fb.control(false));
    });

    const emergencyProceduresGroup = this.fb.group(
      {},
      {
        validators: [simpleCheckboxValidator(1)], // Require at least 1 emergency procedure
      }
    );
    this.emergencyProceduresCheckboxes.forEach((checkbox) => {
      emergencyProceduresGroup.addControl(
        checkbox.name,
        this.fb.control(false)
      );
    });

    this.form = this.fb.group({
      basic: this.fb.group({
        date: [this.model.date, Validators.required],
        project: [this.model.project, Validators.required],
        jobsiteAddress: [this.model.jobsiteAddress],
      }),
      fallHazards: this.fb.group({
        hazards: fallHazardsGroup,
        otherFallHazardSpecify: [this.model.otherFallHazardSpecify],
      }),
      fallProtections: this.fb.group({
        restraint: fallRestraintGroup,
        otherFallRestraintSpecify: [this.model.otherFallRestraintSpecify],
        articulatedLiftsSafetyHarness: [
          this.model.articulatedLiftsSafetyHarness,
        ],
        employeesTrained: [this.model.employeesTrained, Validators.required],
        documentationAvailable: [this.model.documentationAvailable],
        alternateDocumentation: [this.model.alternateDocumentation],
      }),
      overheadProtection: this.fb.group({
        assemblyDescription: [this.model.assemblyDescription],
        maintenanceDescription: [this.model.maintenanceDescription],
        responsiblePersonName: [this.model.responsiblePersonName],
        handling: materialHandlingGroup,
        otherMaterialHandlingSpecify: [this.model.otherMaterialHandlingSpecify],
        protection: overheadProtectionGroup,
        otherOverheadProtectionSpecify: [
          this.model.otherOverheadProtectionSpecify,
        ],
      }),

      emergencyProcedures: this.fb.group({
        procedures: emergencyProceduresGroup,
        otherEmergencyProcedureSpecify: [
          this.model.otherEmergencyProcedureSpecify,
        ],
        additionalSafetyMeasures: [this.model.additionalSafetyMeasures],
      }),
    });

    // Set up conditional validators for "other" fields
    this.setupConditionalValidators();
  }

   protected override setupDefaultStepValidation(): void {
    // Skip validation setup if noFormRequirements is true
    if (!this.shouldApplyFormRequirements()) {
      return;
    }
    // Always validate these steps
    this.setupStepValidation('basic', this.basicFormGroup);
    this.setupStepValidation('fall-hazards', this.fallHazardsFormGroup);
    this.setupStepValidation('fall-protections', this.fallProtectionsFormGroup);

    // Handle conditional steps and validators based on employeesTrained
    this.setupEmployeesTrainedValidation();
  }

  private setupEmployeesTrainedValidation(): void {
    this.fallProtectionsFormGroup.get('employeesTrained')?.valueChanges.subscribe(isTrained => {
      // Update requiredFields object
      this.requiredFields = {
        ...this.requiredFields,
        // Overhead Protection fields
        assemblyDescription: isTrained === 'Yes',
        maintenanceDescription: isTrained === 'Yes',
        responsiblePersonName: isTrained === 'Yes',
   
        // Emergency Procedures fields
        otherEmergencyProcedureSpecify: isTrained === 'Yes',
        additionalSafetyMeasures: isTrained === 'Yes'
      };

      // Update validators for all controls in overhead protection form group
      const overheadControls = [
        'assemblyDescription',
        'maintenanceDescription',
        'responsiblePersonName',

      ];

      overheadControls.forEach(controlName => {
        const control = this.overheadProtectionFormGroup.get(controlName);
        if (control) {
          if (isTrained === 'Yes') {
            control.setValidators([Validators.required]);
          } else {
            control.clearValidators();
          }
          control.updateValueAndValidity();
        }
      });

      // Update validators for all controls in emergency procedures form group
      const emergencyControls = [
        'additionalSafetyMeasures'
      ];

      emergencyControls.forEach(controlName => {
        const control = this.emergencyProceduresFormGroup.get(controlName);
        if (control) {
          if (isTrained === 'Yes') {
            control.setValidators([Validators.required]);
          } else {
            control.clearValidators();
          }
          control.updateValueAndValidity();
        }
      });

      // Handle step management - filter steps based on training status
      if (isTrained === 'Yes') {
        // Show all steps including conditional ones
        this.steps = FallProtectionPlan.getFormSteps();
        // Setup validation for additional steps
        this.setupStepValidation('overhead-protection', this.overheadProtectionFormGroup);
        this.setupStepValidation('emergency-procedures', this.emergencyProceduresFormGroup, {
          signatureIndex: [0]
        });
      } else {
        // Show only the first 3 steps (basic, fall-hazards, fall-protections)
        this.steps = FallProtectionPlan.getFormSteps().slice(0, 3);
        // If we're currently on a removed step, go back to the last valid step
        if (this.currentStep() === 'overhead-protection' || this.currentStep() === 'emergency-procedures') {
          this.currentStep.set('fall-protections');
        }
      }
    });
  }

  private setupConditionalValidators(): void {
    // Fall Hazards
    this.fallHazardsCheckboxesFormGroup.get('otherFallHazard')?.valueChanges.subscribe(checked => {
      const control = this.fallHazardsFormGroup.get('otherFallHazardSpecify');
      if (checked) {
        control?.setValidators([Validators.required]);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity();
    });

    // Fall Restraint
    this.fallRestraintCheckboxesFormGroup.get('otherFallRestraint')?.valueChanges.subscribe(checked => {
      const control = this.fallProtectionsFormGroup.get('otherFallRestraintSpecify');
      if (checked) {
        control?.setValidators([Validators.required]);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity();
    });

    // Material Handling
    this.materialHandlingCheckboxesFormGroup.get('otherMaterialHandling')?.valueChanges.subscribe(checked => {
      const control = this.overheadProtectionFormGroup.get('otherMaterialHandlingSpecify');
      if (checked) {
        control?.setValidators([Validators.required]);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity();
    });

    // Overhead Protection
    this.overheadProtectionCheckboxesFormGroup.get('otherOverheadProtection')?.valueChanges.subscribe(checked => {
      const control = this.overheadProtectionFormGroup.get('otherOverheadProtectionSpecify');
      if (checked) {
        control?.setValidators([Validators.required]);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity();
    });

    // Emergency Procedures
    this.emergencyProceduresCheckboxesFormGroup.get('otherEmergencyProcedure')?.valueChanges.subscribe(checked => {
      const control = this.emergencyProceduresFormGroup.get('otherEmergencyProcedureSpecify');
      if (checked) {
        control?.setValidators([Validators.required]);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity();
    });
  }

  // Checkbox data getters
  getFallHazardsCheckboxes(): { name: string; label: string }[] {
    return [
      { name: 'perimeterEdge', label: this.fieldMappings['perimeterEdge'] },
      {
        name: 'elevatorOpenings',
        label: this.fieldMappings['elevatorOpenings'],
      },
      {
        name: 'stairwayOpenings',
        label: this.fieldMappings['stairwayOpenings'],
      },
      {
        name: 'ventMechanicalOpenings',
        label: this.fieldMappings['ventMechanicalOpenings'],
      },
      { name: 'openSidedFloors', label: this.fieldMappings['openSidedFloors'] },
      {
        name: 'articulatedSnorkelLifts',
        label: this.fieldMappings['articulatedSnorkelLifts'],
      },
      {
        name: 'unprotectedSkylights',
        label: this.fieldMappings['unprotectedSkylights'],
      },
      { name: 'otherFallHazard', label: this.fieldMappings['otherFallHazard'] },
    ];
  }

  getFallRestraintCheckboxes(): { name: string; label: string }[] {
    return [
      {
        name: 'safetyHarnessLanyards',
        label: this.fieldMappings['safetyHarnessLanyards'],
      },
      { name: 'horizontalLines', label: this.fieldMappings['horizontalLines'] },
      { name: 'guardrails', label: this.fieldMappings['guardrails'] },
      { name: 'warningLines', label: this.fieldMappings['warningLines'] },
      {
        name: 'otherFallRestraint',
        label: this.fieldMappings['otherFallRestraint'],
      },
    ];
  }

  getArticulatedLiftsCheckboxes(): { name: string; label: string }[] {
    return [
      { name: 'articulatedLiftsSafetyHarness', label: this.fieldMappings['articulatedLiftsSafetyHarness'] },
    ];
  }

  getMaterialHandlingCheckboxes(): { name: string; label: string }[] {
    return [
      { name: 'cranes', label: this.fieldMappings['cranes'] },
      { name: 'forklifts', label: this.fieldMappings['forklifts'] },
      {
        name: 'otherMaterialHandling',
        label: this.fieldMappings['otherMaterialHandling'],
      },
    ];
  }

  getOverheadProtectionCheckboxes(): { name: string; label: string }[] {
    return [
      {
        name: 'limitOverheadHazards',
        label: this.fieldMappings['limitOverheadHazards'],
      },
      { name: 'materialSecured', label: this.fieldMappings['materialSecured'] },
      {
        name: 'equipmentSecured',
        label: this.fieldMappings['equipmentSecured'],
      },
      { name: 'barricading', label: this.fieldMappings['barricading'] },
      {
        name: 'warningSignsPosted',
        label: this.fieldMappings['warningSignsPosted'],
      },
      {
        name: 'hardHatsRequired',
        label: this.fieldMappings['hardHatsRequired'],
      },
      {
        name: 'toeboardsInstalled',
        label: this.fieldMappings['toeboardsInstalled'],
      },
      {
        name: 'otherOverheadProtection',
        label: this.fieldMappings['otherOverheadProtection'],
      },
    ];
  }

  getEmergencyProceduresCheckboxes(): { name: string; label: string }[] {
    return [
      { name: 'initiate911', label: this.fieldMappings['initiate911'] },
      {
        name: 'utilizeLiftTruck',
        label: this.fieldMappings['utilizeLiftTruck'],
      },
      { name: 'utilizeBoomLift', label: this.fieldMappings['utilizeBoomLift'] },
      { name: 'erectLadders', label: this.fieldMappings['erectLadders'] },
      { name: 'useDroplines', label: this.fieldMappings['useDroplines'] },
      {
        name: 'assistEmergencyTeam',
        label: this.fieldMappings['assistEmergencyTeam'],
      },
      {
        name: 'suspensionTraumaStraps',
        label: this.fieldMappings['suspensionTraumaStraps'],
      },
      {
        name: 'otherEmergencyProcedure',
        label: this.fieldMappings['otherEmergencyProcedure'],
      },
    ];
  }

  // Form group getters
  get basicFormGroup(): FormGroup {
    return this.form.get('basic') as FormGroup;
  }

  get fallHazardsFormGroup(): FormGroup {
    return this.form.get('fallHazards') as FormGroup;
  }

  get fallHazardsCheckboxesFormGroup(): FormGroup {
    return this.fallHazardsFormGroup.get('hazards') as FormGroup;
  }

  get fallProtectionsFormGroup(): FormGroup {
    return this.form.get('fallProtections') as FormGroup;
  }

  get fallRestraintCheckboxesFormGroup(): FormGroup {
    return this.fallProtectionsFormGroup.get('restraint') as FormGroup;
  }

  get overheadProtectionFormGroup(): FormGroup {
    return this.form.get('overheadProtection') as FormGroup;
  }

  get overheadProtectionCheckboxesFormGroup(): FormGroup {
    return this.overheadProtectionFormGroup.get('protection') as FormGroup;
  }

  get emergencyProceduresFormGroup(): FormGroup {
    return this.form.get('emergencyProcedures') as FormGroup;
  }

  get emergencyProceduresCheckboxesFormGroup(): FormGroup {
    return this.emergencyProceduresFormGroup.get('procedures') as FormGroup;
  }

  get materialHandlingCheckboxesFormGroup(): FormGroup {
    return this.overheadProtectionFormGroup.get('handling') as FormGroup;
  }

  protected override getFormData(): FallProtectionPlan {
    const formValue = this.form.value;
    
    const flattenedData = {
      ...formValue.basic,
      ...formValue.fallHazards.hazards,
      otherFallHazardSpecify: formValue.fallHazards.otherFallHazardSpecify,
      ...formValue.fallProtections.restraint,
      otherFallRestraintSpecify: formValue.fallProtections.otherFallRestraintSpecify,
      articulatedLiftsSafetyHarness: formValue.fallProtections.articulatedLiftsSafetyHarness,
      employeesTrained: formValue.fallProtections.employeesTrained,
      documentationAvailable: formValue.fallProtections.documentationAvailable,
      alternateDocumentation: formValue.fallProtections.alternateDocumentation,
    };

    // Only include additional data if employees are trained
    if (formValue.fallProtections.employeesTrained === 'Yes') {
      Object.assign(flattenedData, {
        ...formValue.overheadProtection.protection,
        otherOverheadProtectionSpecify: formValue.overheadProtection.otherOverheadProtectionSpecify,
        assemblyDescription: formValue.overheadProtection.assemblyDescription,
        maintenanceDescription: formValue.overheadProtection.maintenanceDescription,
        responsiblePersonName: formValue.overheadProtection.responsiblePersonName,
        ...formValue.overheadProtection.handling,
        otherMaterialHandlingSpecify: formValue.overheadProtection.otherMaterialHandlingSpecify,
        ...formValue.emergencyProcedures.procedures,
        otherEmergencyProcedureSpecify: formValue.emergencyProcedures.otherEmergencyProcedureSpecify,
        additionalSafetyMeasures: formValue.emergencyProcedures.additionalSafetyMeasures,
      });
    }
    
    const fallProtectionPlan = new FallProtectionPlan(flattenedData);
    const updatedFallProtectionPlan = this.updateModelWithAttachments(fallProtectionPlan);
    return updatedFallProtectionPlan;
  }


}
